/* eslint-disable  */
import { ArrowDown, ArrowUp, Eye, SquareArrowOutUpRight } from "lucide-react";
import { dispatchSummaryCostOutletBasedColumns, dispatchSummaryWeightOutletBasedColumns, issuedSummaryCostProductBasedColumns, issuedSummaryWeightProductBasedColumns, producedSummaryCostOutletBasedColumns, producedSummaryWeightOutletBasedColumns, restaurantIndentCostSectionBasedColumns, restaurantIndentWeightSectionBasedColumns } from "../table.constants";
import { urls } from "@/Constants";
import { commaForAmount, extractNumeric, getConvertedData, getDynamicColumnMapAndColumns, getDynamicWastageColumns, getFilteredData, getfilteredWastageData, getFirstChildText, getRowIndex, getTextColor, getTextFromElement, getTooltipTitle, issuedWeightProductBasedData } from "../helper";
import assummedIcon from '@/Images/noweigh.svg';
import noMachineIcon from '@/Images/scan.jpeg';
import { useCallback, useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import inventoryAnalyticsServices from "@/Services/Analytics Services/inventory-analytics.services";
import { getHigherUnitName, handleImagePreview } from "@/Helpers";

export const useTableData = (props) => {
	const {
		parentTab, selectedTab, tableTab, active, filters, data, producedData, search, sort, actualProductPrice, idealCost,
		subParentTab,
	} = props;

	const fromDate = filters?.dateRange?.from ? new Date(filters?.dateRange?.from) : null;

	const updatedFilters = {
		...filters,
		dateRange: {
			...filters?.dateRange,
			from: fromDate
				? new Date(fromDate?.setDate(fromDate?.getDate() - 1))?.toISOString()
				: null,
		},
	};

	const [poInfoById, setPoInfoById] = useState({});

	const getPoData = async (poIds) => {
		let ids = null;
		if (Array.isArray(poIds) && poIds?.length > 0) {
			ids = poIds
		} else {
			ids = [poIds]
		}
		try {
			const req = {
				data: {
					filters: {
						purchaseOrderIds: ids,
					}
				}
			}
			const response = await inventoryAnalyticsServices?.fetchPoList({ req });
			if (response?.statusCode === 200) {
				return response?.data;
			}
		} catch (error) {
			console.log(error);
		}
	}

	useEffect(() => {
		if (parentTab?.id !== 3) return;
		const run = async () => {
			try {
				const uniqueIds = new Set();
				Object.values(data?.items || {}).forEach((item) => {
					Object.values(item?.pods || {}).forEach((pod) => {
						(pod?.poIds || []).forEach((id) => uniqueIds.add(id));
					});
				});
				const ids = Array.from(uniqueIds).filter((id) => poInfoById[id] == null);
				if (ids.length > 0) {
					const list = await getPoData(ids);
					if (Array.isArray(list)) {
						setPoInfoById((prev) => {
							const map = { ...prev };
							list.forEach((po) => {
								const key = po?.id ?? po?.purchaseOrderId;
								if (key != null) map[key] = po;
							});
							return map;
						});
					}
				}
			} catch (e) {
				console.log(e);
			}
		};
		run();
	}, [parentTab?.id, data]);

	const getNoMachineIcon = () => {
		return (
			<div className='flex bg-white rounded-md overflow-hidden gap-1 relative items-center'>
				<div className='z-10 absolute flex justify-center w-full h-full'>
					<div className='h-full w-[2px] bg-red-700 -rotate-45' />
				</div>
				<img src={noMachineIcon} alt='noMachineIcon' className='inline w-full h-full' />
			</div>
		)
	}

	const getTableInfoForIssuedSummary = () => {
		switch (selectedTab) {
			case 'weight':
				return getWeightBasedTableData();
			case 'cost':
				return getCostBasedTableData();
			case 'percentage':
				return getPercentageBasedTableData();
		}
	};

	const getWeightBasedTableData = () => {
		switch (tableTab) {
			case 'products':
				return getProductsWeightTableData();
			case 'rawMaterials':
				return getRawMaterialsWeightTableData();
		}
	};

	const getCostBasedTableData = () => {
		switch (tableTab) {
			case 'products':
				return getProductsCostTableData();
			case 'rawMaterials':
				return getRawMaterialsCostTableData();
		}
	};

	const getVariance = (productData) => {
		let totalHighVariance = 0;
		let totalLowVariance = 0;

		Object.values(productData?.pods || {})?.forEach((item) => {
			const orderCost = item?.orderQty * item?.rate;
			const diffOrderedCostFullfilledCost = Number(Math.abs(orderCost - item?.fullfilledCost).toFixed(0));

			if (diffOrderedCostFullfilledCost != 0 && Math.abs((item?.fullfilledCost - orderCost) / orderCost) > 0.05) {
				if (orderCost < item?.fullfilledCost) {
					totalHighVariance += diffOrderedCostFullfilledCost;
				} else {
					totalLowVariance += diffOrderedCostFullfilledCost;
				}
			}
		});

		return totalHighVariance > totalLowVariance ? 'high' : 'low';
	};

	const getIssuedProductBasedData = (customRowData = {}) => {
		const { sortColumnName: sortCol, sortType, sortAttribute } = sort;
		let result = Object.values(producedData?.find((item) => item?.mealType === active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase())?.items ?? []) ?? [];
		if (sortCol && sortType && customRowData[sortCol]) {
			const renderFn = customRowData[sortCol];
			result.sort((a, b) => {
				const aVal = getTextFromElement(renderFn(a));
				const bVal = getTextFromElement(renderFn(b));
				if (sortAttribute === 'number') {
					const numA = extractNumeric(aVal);
					const numB = extractNumeric(bVal);

					if (!isNaN(numA) && !isNaN(numB)) {
						return sortType === 'ascending' ? numA - numB : numB - numA;
					}
				}
				return sortType === 'ascending'
					? String(aVal).localeCompare(String(bVal))
					: String(bVal).localeCompare(String(aVal));

			});
		}
		const searchTerm = search?.searchValue?.toLowerCase()?.trim();
		if (searchTerm) {
			result = result.filter(
				(item) => item?.itemName?.toLowerCase()?.trim()?.includes(searchTerm) ||
					item?.itemClientVariantId?.toLowerCase()?.trim()?.includes(searchTerm)
			);
		}
		return result;
	};

	const getProductsWeightTableData = () => {
		const customRowData = {
			productName: (product) => {
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>{product?.itemName}</p>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			},
			pricePerUnit: (product) => {
				// const rate = Object.values(product?.pods)?.[0]?.ingData?.[0]?.rate;
				const unit = Object.values(product?.pods)?.[0]?.ingData?.[0]?.unit;
				const rate = actualProductPrice?.[product?.itemName] || 0;
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>
							₹
							{parseFloat(rate)?.toFixed(2)}
							/
							{unit}
						</p>
					</div>
				);
			},
			orderedQuantity: (product) => {
				const totalOrderQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty || 0), 0)), 0);
				const unit = Object.values(product?.pods || {})?.[0]?.ingData?.[0]?.unit;
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>
							{totalOrderQty?.toFixed(2)}
							{' '}
							{unit}
						</p>
					</div>
				);
			},
			issuedQuantity: (product) => {
				const totalIssuedQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0)), 0);
				const unit = Object.values(product?.pods || {})?.[0]?.ingData?.[0]?.unit;
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>
							{totalIssuedQty?.toFixed(2)}
							{' '}
							{unit}
						</p>
					</div>
				);
			},
			issuingPercentage: (product) => {
				const totalIssuedQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0)), 0);
				const totalOrderQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty || 0), 0)), 0);
				const issuingPercentage = totalOrderQty > 0 ? (totalIssuedQty / totalOrderQty) * 100 : 0;
				return (
					<div className='flex items-center gap-1 justify-start'>
						<Tooltip title={getTooltipTitle(totalIssuedQty, totalOrderQty)}>
							<p className={`text-[12px] ${getTextColor(totalIssuedQty, totalOrderQty)}`}>
								{issuingPercentage?.toFixed(2)}
								%
							</p>
						</Tooltip>
					</div>
				);
			},
			action: (product) => {
				const producedDataList = producedData?.find((item) => item?.mealType === active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase())?.items ?? {};
				const convertedData = getConvertedData(data, 'issuedSummary', producedDataList);
				const rowIndex = getRowIndex(producedDataList, product?.itemId);
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertedData));
				return (
					<SquareArrowOutUpRight
						size={16}
						className='cursor-pointer transition-transform duration-200 hover:scale-125'
						onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=issued&name=${product?.itemName}&meal=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${rowIndex}`, '_blank')}
					/>
				);
			},
		};

		return {
			data: getIssuedProductBasedData(customRowData),
			tableRows: customRowData,
			tableColumns: issuedSummaryWeightProductBasedColumns,
		};
	};

	const getProductsCostTableData = () => {
		const customRowData = {
			productName: (product) => {
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>{product?.itemName}</p>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			},
			pricePerUnit: (product) => {
				// const rate = Object.values(product?.pods)?.[0]?.ingData?.[0]?.rate;
				const rate = actualProductPrice?.[product?.itemName] || 0;
				const unit = Object.values(product?.pods)?.[0]?.ingData?.[0]?.unit;
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>
							₹
							{parseFloat(rate)?.toFixed(2)}
							/
							{unit}
						</p>
					</div>
				);
			},
			orderedCost: (product) => {
				const totalOrderCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty * (idealCost?.[product?.itemName] || 0) || 0), 0)), 0);
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>
							₹
							{totalOrderCost?.toFixed(2)}
						</p>
					</div>
				);
			},
			issuedCost: (product) => {
				const totalIssuedCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty * (actualProductPrice?.[product?.itemName] || 0) || 0), 0)), 0);
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>
							₹
							{totalIssuedCost?.toFixed(2)}
						</p>
					</div>
				);
			},
			idealCost: (product) => {
				const totalOrderCost = idealCost?.[product?.itemName] || 0;
				//  Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty * item?.rate || 0), 0)), 0);
				const unit = Object.values(product?.pods || {})?.[0]?.ingData?.[0]?.unit;
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px] text-nowrap'>
							₹
							{totalOrderCost?.toFixed(2)}
						</p>
					</div>
				);
			},
			issuedCostPercentage: (product) => {
				const totalOrderCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty * (idealCost?.[product?.itemName] || 0) || 0), 0)), 0);
				const totalIssuedCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty * (actualProductPrice?.[product?.itemName] || 0) || 0), 0)), 0);
				const percentage = totalOrderCost > 0 ? (totalIssuedCost / totalOrderCost) * 100 : 0;
				return (
					<div className='flex items-center gap-1 justify-start'>
						<p className='text-[12px]'>
							{percentage?.toFixed(2)}
							%
						</p>
					</div>
				);
			},
			variance: (product) => {
				const orderCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty * (idealCost?.[product?.itemName] || 0) || 0), 0)), 0);
				const issuedCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty * (actualProductPrice?.[product?.itemName] || 0) || 0), 0)), 0);
				const diffOrderedCostFullfilledCost = Number(Math.abs((orderCost) - (issuedCost)).toFixed(0));
				return orderCost > issuedCost ? (
					<Tooltip title='With In Threshold'>
						<span className='flex items-center gap-1 text-[12px] text-green-500 text-nowrap'>
							<ArrowDown size={12} className='text-green-500' />
							{diffOrderedCostFullfilledCost}
						</span>
					</Tooltip>
				) : (
					<Tooltip title='High Variance'>
						<span className='flex items-center gap-1 text-[12px] text-red-500 text-nowrap'>
							<ArrowUp size={12} className='text-red-500' />
							{diffOrderedCostFullfilledCost}
						</span>
					</Tooltip>
				);
			},
			action: (product) => {
				let convertData = [];
				const podMap = new Map();
				const producedDataList = producedData?.find((item) => item?.mealType === active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase())?.items ?? {};
				Object.entries(data?.items || {}).forEach(([itemId, item]) => {
					Object.values(item?.pods || {}).forEach((pod) => {
						if (pod?.podId) {
							const productData = Object.values(producedDataList)?.find((item) => item?.itemName === pod?.podName) ?? {};
							const totalIssuedQty = Object.values(productData?.pods || {})?.reduce?.((acc, item) => acc + item?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0), 0);
							const totalOrderQty = Object.values(productData?.pods || {})?.reduce?.((acc, item) => acc + item?.ingData?.reduce((acc, item) => acc + (item?.orderQty || 0), 0), 0);
							const issuingPercentage = totalOrderQty > 0 ? (totalIssuedQty / totalOrderQty) * 100 : 0;
							const unit = Object.values(productData?.pods || {})?.[0]?.ingData?.[0]?.unit;
							const totalFulfilledCost = Object.values(productData?.pods || {})?.reduce?.((acc, item) => acc + item?.ingData?.reduce((acc, item) => acc + (item?.fullfilledCost || 0), 0), 0);
							const totalOrderCost = Object.values(productData?.pods || {})?.reduce?.((acc, item) => acc + item?.ingData?.reduce((acc, item) => acc + ((item?.orderQty * item?.rate) || 0), 0), 0);
							const variance = getVariance(productData);
							if (!podMap.has(pod.podId)) {
								podMap.set(pod.podId, {
									podId: pod.podId,
									podName: pod.podName,
									kitchenName: pod.kitchenName,
									scheduledAt: pod.scheduledAt,
									meal: active?.header,
									fullfilledPercentage: issuingPercentage?.toFixed(2),
									totalWeight: `${totalIssuedQty?.toFixed(2)} ${unit} / ${totalOrderQty?.toFixed(2)} ${unit}`,
									totalCost: `${totalFulfilledCost?.toFixed(2)} ${unit} / ${totalOrderCost?.toFixed(2)} ${unit}`,
									idealCost: `${(totalOrderCost / totalOrderQty)?.toFixed(2)} /${unit}`,
									variance,
									allIngredients: [],
								});
							}
							podMap.get(pod.podId).allIngredients.push({
								ingName: item?.itemName,
								itemClientVariantId: item?.itemClientVariantId,
								...pod,
							});
						}
					});
				});
				convertData = Array.from(podMap.values());
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertData));
				return (
					<SquareArrowOutUpRight
						size={16}
						className='cursor-pointer transition-transform duration-200 hover:scale-125'
						onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=issued&name=${product?.itemName}&filters=${encodeURIComponent(JSON.stringify(filters))}`, '_blank')}
					/>
				);
			},
		};
		return {
			data: getIssuedProductBasedData(customRowData),
			tableRows: customRowData,
			tableColumns: issuedSummaryCostProductBasedColumns,
		};
	};

	const getColumnWiseTotal = (columnKey, parentTab, subTab, type) => {
		if (parentTab === 'issuedSummary' && subTab === 'rawMaterial') {
			if (type === 'weight') {
				let totalFulfilledQty = 0;
				let totalOrderQty = 0;
				let unit = '';
				Object.values(data?.items)?.forEach((item) => {
					const pod = item?.pods?.[columnKey];
					const ingValue = pod?.ingData;
					if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
					totalFulfilledQty += ingValue?.reduce((acc, item) => acc + (item?.skuName === 'Water' ? item?.fullfilledQty === 0 ? item?.orderQty : item?.fullfilledQty || 0 : item?.fullfilledQty || 0), 0);
					totalOrderQty += ingValue?.reduce((acc, item) => acc + (item?.orderQty || 0), 0);
					unit = ingValue?.reduce((acc, item) => acc + (item?.unit || ''), '');
				});
				return (
					<div className='text-[12px]'>
						{parseFloat(totalFulfilledQty)?.toFixed(2)}
						{' '}
						kg /
						{' '}
						{parseFloat(totalOrderQty)?.toFixed(2)}
						{' '}
						kg
					</div>
				);
			}
			if (type === 'cost') {
				const assumedCost = [];
				let totalWeight = 0;
				let totalWeightCount = 0;
				let totalOrderCost = 0;
				let totalCost = 0;
				let totalPercent = 0;
				let allDashes = false;
				let totalPackaged = 0;
				let idealCostPerKg = 0;
				let orderQty = 0;

				Object.values(data?.items)?.forEach((item) => {
					const pod = item?.pods?.[columnKey];
					const ingValue = pod?.ingData;
					if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
					ingValue.forEach((item) => {
						if (item) {
							const filled = parseFloat(item?.fullfilledQty) || 0;
							const ordered = parseFloat(item?.orderQty) || 0;
							assumedCost.push(item?.fullfilledQty);
							totalWeight += filled;
							totalWeightCount += ordered;
							totalOrderCost += (ordered * (parseFloat(item?.rate) || 0)) || 0;
							totalCost += parseFloat(item?.fullfilledCost);
							totalPercent += item?.fullfilledPercentage;
							allDashes = false;
						}
					});
					totalPackaged = pod?.packaged;
					// totalOrderCost = pod?.orderQty * (actualProductPrice?.[pod?.podName] || 0);
					idealCostPerKg = parseFloat((totalOrderCost / pod?.orderQty).toFixed(2));
					orderQty = pod?.orderQty;
				});
				if (totalCost > 0 || totalOrderCost > 0) {
					return (
						<div className='flex flex-col gap-1 p-2 '>
							<span className='flex text-[12px] font-medium text-gray-700'>
								Total:
								<span className='text-blue-600 font-semibold ml-1'>
									₹{totalCost.toFixed(2) ?? '-'}
								</span>
								<span className='text-gray-500 mx-1'>/</span>
								<span className='text-gray-600'>
									₹{(totalOrderCost).toFixed(2)}
								</span>
							</span>
							<div className='flex items-center justify-between gap-4'>
								<div className='flex flex-col gap-1 flex-1'>
									<span className='flex items-center gap-2 text-[11px] font-medium text-blue-600 text-nowrap'>
										<span className='text-gray-700'>Actual:</span>
										<span>₹{totalPackaged ? (totalCost / (totalPackaged)).toFixed(2) : '-'}/kg</span>
										<span className='text-gray-600'>({totalPackaged} kg)</span>
									</span>
									<span className='flex items-center gap-2 text-[11px] font-medium text-gray-600 text-nowrap'>
										<span>Ideal:</span>
										<span>₹{idealCostPerKg}/kg</span>
										<span className='text-gray-600'>({orderQty} kg)</span>
									</span>
								</div>
								{assumedCost?.length > 0 && (() => {
									const zeroCount = assumedCost.filter((x) => x === 0).length;
									const allZero = zeroCount === assumedCost.length;
									const moreThanOneZero = zeroCount > 1 && !allZero;
									return (
										<div className='flex items-center gap-2 flex-shrink-0'>
											{moreThanOneZero && (
												<Tooltip title='Skipped by operator'>
													<img
														src={assummedIcon}
														alt='assummedIcon'
														className='w-5 h-5 opacity-80'
													/>
												</Tooltip>
											)}
											{allZero && (
												<Tooltip title='All Ingredients skipped by operator'>
													<div className='bg-red-400 text-black w-6 h-6 rounded-full font-bold text-xs flex items-center justify-center shadow-sm'>
														!
													</div>
												</Tooltip>
											)}
										</div>
									);
								})()}
							</div>
						</div>
					);
				}
			}
		}
		if (parentTab === 'producedSummary' && subTab === 'products') {
			if (type === 'weight') {
				let totalFulfilledQty = 0;
				let totalOrderQty = 0;
				let unit = '';
				Object.values(data?.items)?.forEach((item) => {
					const pod = item?.pods?.[columnKey];
					const ingValue = pod?.ingData;
					if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
					totalFulfilledQty += ingValue?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0);
					totalOrderQty += ingValue?.reduce((acc, item) => acc + (item?.orderQty || 0), 0);
					unit = ingValue?.reduce((acc, item) => acc + (item?.unit || ''), '');
				});
				return (
					<div className='text-[12px]'>
						{parseFloat(totalFulfilledQty)?.toFixed(2)}
						{' '}
						kg /
						{' '}
						{parseFloat(totalOrderQty)?.toFixed(2)}
						{' '}
						kg
					</div>
				);
			}
			if (type === 'cost') {
				let totalCost = 0;
				let totalOrderCost = 0;
				const assumedCost = [];
				let unit = '';
				Object.values(data?.items)?.forEach((item) => {
					const pod = item?.pods?.[columnKey];
					const ingValue = pod?.ingData;
					if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
					ingValue.forEach((item) => {
						const filled = parseFloat(item?.fullfilledQty) || 0;
						const ordered = parseFloat(item?.orderQty) || 0;
						assumedCost.push(item?.fullfilledQty);
						totalOrderCost += (ordered * (parseFloat(item?.rate) || 0)) || 0;
					});
					totalCost += pod?.packagedCost;
					unit = pod?.unit;
				});
				if (totalCost > 0 || totalOrderCost > 0) {
					return (
						<div className='flex flex-col gap-1'>
							<span className='text-[12px] text-nowrap'>
								Total : ₹
								{commaForAmount(totalCost.toFixed(2)) ?? '-'}
								{' '}
								/ ₹
								{commaForAmount((totalOrderCost).toFixed(2))}
							</span>
						</div>
					);
				}
				return '-';
			}
		}
	};

	const getRawMaterialsWeightTableData = () => {
		const { colMap, columns } = getDynamicColumnMapAndColumns(data);

		// columns custom
		const customCol = (column) => {
			if (column?.key === 'ingredientName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			if (column?.key === 'total') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div className='flex flex-col gap-1 text-[12px]'>
					{column?.title}
					{getColumnWiseTotal(column?.key, 'issuedSummary', 'rawMaterial', 'weight')}
				</div>
			);
		};
		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const totalFullfilledQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0)), 0);
						return parseFloat(totalFullfilledQty)?.toFixed(2);
					}
				}
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			sortInfo[mapKey] = {
				type: 'number',
				customValue: true,
				value: (product) => {
					const pod = product?.pods?.[mapKey];
					const ingValue = pod?.ingData;
					return ingValue?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0);
				}
			}
		}
		// rowData
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				customRowData[key] = (product) => (
					<div className='flex items-center justify-start'>
						<span className='text-[12px]'>{product?.itemName}</span>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			} else if (key === 'total') {
				customRowData[key] = (product) => {
					const totalFullfilledQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.skuName === 'Water' ? item?.fullfilledQty === 0 ? item?.orderQty : item?.fullfilledQty || 0 : item?.fullfilledQty || 0), 0)), 0);
					const totalOrderQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty || 0), 0)), 0);
					const unit = Object?.values(product?.pods || {})?.[0]?.ingData?.[0]?.unit;
					return (
						<p className='text-[12px] text-nowrap flex items-center gap-1'>
							{parseFloat(totalFullfilledQty)?.toFixed(2)}
							{' '}
							{unit}
							{' '}
							/
							{' '}
							{parseFloat(totalOrderQty)?.toFixed(2)}
							{' '}
							{unit}
							{totalFullfilledQty === 0 && (
								<Tooltip title='Skipped by operator'>
									<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px] inline-block' />
								</Tooltip>
							)}
						</p>
					);
				};
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							return (
								<div className='flex gap-2 text-nowrap items-center justify-between'>
									<div className='flex gap-2 items-center'>
										<span className='text-blue-500 text-[12px]'>
											{item?.skuName === 'Water' ? Number(item?.fullfilledQty === 0 ? item?.orderQty : item?.fullfilledQty || 0)?.toFixed(2) : Number(item?.fullfilledQty || 0)?.toFixed(2)}
											{' '}
											{item?.unit}
										</span>
										{' '}
										/
										<span className='text-[12px]'>
											{Number(item?.orderQty || 0)?.toFixed(2)}
											{' '}
											{item?.unit}
										</span>
									</div>
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${item?.recipeName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(updatedFilters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
									{item?.skipMeasurement && (
										<Tooltip title='Skipped by settings'>
											<span className='w-5 h-5 gap-1 items-center'>
												{getNoMachineIcon()}
											</span>
										</Tooltip>
									)}
									{
										item?.fullfilledQty === 0 && (
											<Tooltip title='Skipped by operator'>
												<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px]' />
											</Tooltip>
										)
									}
								</div>
							);
						})}
					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	};

	const getProducedSfgBasedTableData = () => {
		const { colMap, columns } = getDynamicColumnMapAndColumns(data, undefined, undefined, 'sfg');
		const customCol = (column) => {
			if (column?.key === 'intermediateName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			if (column?.key === 'total') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div className='flex flex-col items-center gap-1 justify-center'>
					<p
						className='text-[12px] text-blue-500 text-underline'
						onClick={() => window.open(
							`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: column?.key, name: column?.title }))}`
						)}
					>
						{column?.title}
					</p>
					{getColumnWiseTotal(column?.key, 'producedSummary', 'products', 'weight')}
				</div>
			);
		};
		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'intermediateName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const totalFullfilledQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0)), 0);
						return parseFloat(totalFullfilledQty)?.toFixed(2);
					}
				}
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'intermediateName' || mapKey === 'total') continue;
			sortInfo[mapKey] = {
				type: 'number',
				customValue: true,
				value: (product) => {
					const pod = product?.pods?.[mapKey];
					const ingValue = pod?.ingData;
					return ingValue?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0);
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'intermediateName') {
				customRowData[key] = (product) => (
					<div
						className='flex items-start justify-start'
						onClick={() => window.open(
							`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`
						)}
					>
						<p className='text-[12px] text-blue-500 text-underline'>{product?.itemName}</p>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			}
			if (key === 'total') {
				customRowData[key] = (product) => {
					const totalFullfilledQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0)), 0);
					const totalOrderQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty || 0), 0)), 0);
					const unit = Object?.values(product?.pods || {})?.[0]?.ingData?.[0]?.unit;
					return (
						<p className='text-[12px] text-nowrap flex items-center gap-1'>
							{parseFloat(totalFullfilledQty)?.toFixed(2)}
							{' '}
							{unit}
							{' '}
							/
							{' '}
							{parseFloat(totalOrderQty)?.toFixed(2)}
							{' '}
							{unit}
							{totalFullfilledQty === 0 && (
								<Tooltip title='Skipped by operator'>
									<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px] inline-block' />
								</Tooltip>
							)}
						</p>
					);
				};
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'intermediateName' || mapKey === 'total') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				const haveMoreThan2Ing = ingValue?.length > 2;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							return (
								<div className='flex gap-2 text-nowrap items-start justify-start'>
									<div className='flex gap-2 items-center'>
										<span className='text-blue-500 text-[12px]'>
											{Number(item?.fullfilledQty || 0)?.toFixed(2)}
											{' '}
											{item?.unit}
										</span>
										{' '}
										/
										<span className='text-[12px]'>
											{Number(item?.orderQty || 0)?.toFixed(2)}
											{' '}
											{item?.unit}
										</span>
									</div>
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
									{item?.skipMeasurement && (
										<Tooltip title='Skipped by settings'>
											<span className='w-5 h-5 gap-1 items-center'>
												{getNoMachineIcon()}
											</span>
										</Tooltip>
									)}
									{
										item?.fullfilledQty === 0 && (
											<Tooltip title='Skipped by operator'>
												<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px]' />
											</Tooltip>
										)
									}
								</div>
							);
						})}
						{/* {selectedTab === 'weight' && product?.unit !== 'kg' && !haveMoreThan2Ing && (
					<span className='text-[12px]'>{product?.fullfilledWeight} kg / {product?.orderedWeight} kg</span>
				)} */}

					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	}

	const getRawMaterialsCostTableData = () => {
		const { colMap, columns } = getDynamicColumnMapAndColumns(data, undefined, 'cost');
		// column custom

		const customCol = (column) => {
			if (column?.key === 'ingredientName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			if (column?.key === 'total') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div className='flex flex-col items-center gap-1 justify-center'>
					<p className='text-[12px]'>
						{column?.title}
					</p>
					{getColumnWiseTotal(column?.key, 'issuedSummary', 'rawMaterial', 'cost')}
				</div>
			);
		};

		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const totalFullfilledCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledCost || 0), 0)), 0);
						return parseFloat(totalFullfilledCost)?.toFixed(2);
					}
				}
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			sortInfo[mapKey] = {
				type: 'number',
				customValue: true,
				value: (product) => {
					let diffOrderedCostFullfilledCost = 0;
					const pod = product?.pods?.[mapKey];
					const ingValue = pod?.ingData;
					const orderCost = ingValue?.reduce((acc, item) => acc + (Number((item?.orderQty * item?.rate).toFixed(2)) || 0), 0);
					const fullfilledCost = ingValue?.reduce((acc, item) => acc + (item?.fullfilledCost || 0), 0);
					diffOrderedCostFullfilledCost = ingValue?.reduce((acc, item) => acc + (Number(Math.abs((item?.orderQty * item?.rate) - item?.fullfilledCost).toFixed(0)) || 0), 0);
					if (orderCost && diffOrderedCostFullfilledCost != 0 && Math.abs((fullfilledCost - orderCost) / orderCost) > 0.05) {
						diffOrderedCostFullfilledCost = Number(Math.abs((fullfilledCost - orderCost).toFixed(0)));
					} else {
						diffOrderedCostFullfilledCost = 0;
					}
					if (sort?.sortColumnName === 'costPrice') {
						return ingValue?.reduce((acc, item) => acc + (item?.fullfilledCost || 0), 0);
					}
					if (sort?.sortColumnName === 'variance') {
						return diffOrderedCostFullfilledCost;
					}
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				customRowData[key] = (product) => {
					const pod = product?.pods;
					const rate = Object?.values(pod)?.[0]?.ingData?.[0]?.rate;
					const unit = Object?.values(pod)?.[0]?.ingData?.[0]?.unit;
					return (
						<div className='flex flex-col items-start gap-1 justify-start'>
							<div className='flex justify-start'>
								<p className='text-[12px]'>{product?.itemName}</p>
								<span className='text-[12px] text-gray-500'>
									(
									{product?.itemClientVariantId}
									)
								</span>
							</div>
							<div className='flex gap-1 justify-start'>
								<span className='text-[12px] font-bold'>Price</span>
								<span className='text-[12px]'>
									₹
									{rate}
									{' '}
									/
									{unit}
								</span>
							</div>
						</div>
					);
				};
			}
			if (key === 'total') {
				customRowData[key] = (product) => {
					const pod = product?.pods;
					const totalCost = Object?.values(pod)?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledCost || 0), 0)), 0);
					return (
						<p className='text-[12px] text-blue-500'>
							₹
							{commaForAmount(totalCost)}
						</p>
					);
				};
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				const totalCost = Object?.values(data?.items)?.reduce((acc, item) => acc + (item?.pods?.[mapKey]?.ingData?.reduce((subAcc, ingredient) => subAcc + (ingredient?.fullfilledCost || 0), 0) || 0), 0);
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-start'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							const orderCost = Number((item?.orderQty * item?.rate).toFixed(2));
							const diffOrderedCostFullfilledCost = Number(Math.abs((item?.orderQty * item?.rate) - item?.fullfilledCost).toFixed(0));
							return (
								<div className='flex gap-2 text-nowrap items-start justify-between'>
									<p className='text-[12px] text-blue-500'>
										₹
										{commaForAmount(item?.fullfilledCost)}
									</p>
									<div className='text-[12px] text-blue-500'>
										(
										{(item?.fullfilledCost * 100 / (totalCost || 1))?.toFixed?.(2)}
										%)
									</div>
									<Tooltip title='Indicates (variance) scanned/ordered outside the threshold (±5%)'>
										{orderCost && diffOrderedCostFullfilledCost != 0 && Math.abs((item?.fullfilledCost - orderCost) / orderCost) > 0.05 && (
											orderCost > item?.fullfilledCost ? (
												<span className='flex items-center gap-1 text-[12px] text-green-500'>
													(
													<ArrowDown size={12} className='text-green-500' />
													{diffOrderedCostFullfilledCost}
													)
												</span>
											) : (
												<span className='flex items-center gap-1 text-[12px] text-red-500'>
													(
													<ArrowUp size={12} className='text-red-500' />
													{diffOrderedCostFullfilledCost}
													)
												</span>
											)
										)}
									</Tooltip>
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${item?.recipeName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(updatedFilters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
								</div>
							);
						})}
					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	};

	const getProducedSfgCostBasedTableData = () => {
		const { colMap, columns } = getDynamicColumnMapAndColumns(data, undefined, 'cost', 'sfg');

		const customCol = (column) => {
			if (column?.key === 'intermediateName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			if (column?.key === 'total') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div
					className='flex flex-col items-center gap-1 justify-center'
					onClick={() => window.open(
						`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: column?.key, name: column?.title }))}`
					)}
				>
					<p className='text-[12px] text-blue-500 text-underline'>
						{column?.title}
					</p>
					{getColumnWiseTotal(column?.key, 'producedSummary', 'products', 'cost')}
				</div>
			);
		};


		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'intermediateName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const totalFullfilledCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledCost || 0), 0)), 0);
						return parseFloat(totalFullfilledCost)?.toFixed(2);
					}
				}
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'intermediateName' || mapKey === 'total') continue;
			sortInfo[mapKey] = {
				type: 'number',
				customValue: true,
				value: (product) => {
					let diffOrderedCostFullfilledCost = 0;
					const pod = product?.pods?.[mapKey];
					const ingValue = pod?.ingData;
					const orderCost = ingValue?.reduce((acc, item) => acc + (Number((item?.orderQty * item?.rate).toFixed(2)) || 0), 0);
					diffOrderedCostFullfilledCost = ingValue?.reduce((acc, item) => acc + (Number(Math.abs((item?.orderQty * item?.rate) - pod?.packagedCost).toFixed(0)) || 0), 0);
					if (orderCost && diffOrderedCostFullfilledCost != 0 && Math.abs((pod?.packagedCost - orderCost) / orderCost) > 0.05) {
						diffOrderedCostFullfilledCost = Number(Math.abs((pod?.packagedCost - orderCost).toFixed(0)));
					} else {
						diffOrderedCostFullfilledCost = 0;
					}
					if (sort?.sortColumnName === 'costPrice') {
						return pod?.packagedCost;
					}
					if (sort?.sortColumnName === 'variance') {
						return diffOrderedCostFullfilledCost;
					}
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'intermediateName') {
				customRowData[key] = (product) => {
					const pod = product?.pods;
					const rate = actualProductPrice?.[product?.itemName] ?? Object?.values(pod)?.[0]?.ingData?.[0]?.rate;
					const unit = Object?.values(pod)?.[0]?.ingData?.[0]?.unit;
					return (
						<div
							className='flex flex-col items-start gap-1 justify-start'
							onClick={() => window.open(
								`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`
							)}
						>
							<div className='flex justify-start'>
								<p className='text-blue-500 text-underline text-[12px]'>{product?.itemName}</p>
								<span className='text-[12px] text-gray-500'>
									(
									{product?.itemClientVariantId}
									)
								</span>
							</div>
							<div className='flex gap-1 justify-start'>
								<span className='text-[12px] font-bold'>Price</span>
								<span className='text-[12px]'>
									₹
									{parseFloat(rate)?.toFixed(2)}
									{' '}
									/
									{unit}
								</span>
							</div>
						</div>
					);
				};
			}
			if (key === 'total') {
				customRowData[key] = (product) => {
					const pod = product?.pods;
					const totalCost = Object?.values(pod)?.reduce((acc, pod) => acc + (pod?.packagedCost || 0), 0);
					return (
						<p className='text-[12px] text-blue-500'>
							₹
							{commaForAmount(totalCost)}
						</p>
					);
				};
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'intermediateName' || mapKey === 'total') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							const orderCost = Number((item?.orderQty * item?.rate).toFixed(2));
							const diffOrderedCostFullfilledCost = Number(Math.abs((item?.orderQty * item?.rate) - pod?.packagedCost).toFixed(0));
							return (
								<div className='flex gap-2 text-nowrap items-start justify-between'>
									<p className='text-[12px] text-blue-500'>
										₹
										{commaForAmount(pod?.packagedCost)}
									</p>
									<Tooltip title='Indicates (variance) scanned/ordered outside the threshold (±5%)'>
										{orderCost && diffOrderedCostFullfilledCost != 0 && Math.abs((pod?.packagedCost - orderCost) / orderCost) > 0.05 && (
											orderCost > pod?.packagedCost ? (
												<span className='flex items-center gap-1 text-[12px] text-green-500'>
													(
													<ArrowDown size={12} className='text-green-500' />
													{diffOrderedCostFullfilledCost}
													)
												</span>
											) : (
												<span className='flex items-center gap-1 text-[12px] text-red-500'>
													(
													<ArrowUp size={12} className='text-red-500' />
													{diffOrderedCostFullfilledCost}
													)
												</span>
											)
										)}
									</Tooltip>
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: pod?.podId, name: pod?.podName }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
								</div>
							);
						})}
					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	};

	const getPercentageBasedTableData = () => {
		const excludeColumn = ['total'];
		const { colMap, columns } = getDynamicColumnMapAndColumns(data, excludeColumn);

		const customCol = (column) => {
			if (column?.key === 'ingredientName') {
				return <div className='flex justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return <div className='flex flex-col items-center gap-1 justify-center'><p className='text-[12px]'>{column?.title}</p></div>;
		};

		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			}
			for (const [mapKey, columnKey] of colMap || []) {
				if (mapKey === 'ingredientName' || mapKey === 'total') continue;
				sortInfo[mapKey] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const pod = product?.pods?.[mapKey];
						const ingValue = pod?.ingData;
						return ingValue?.reduce((acc, item) => acc + (item?.fullfilledPercentage || 0), 0);
					}
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				customRowData[key] = (product) => (
					<div className='flex items-start justify-start'>
						<p className='text-[12px]'>{product?.itemName}</p>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-start'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							return (
								<div className='flex items-start justify-between gap-1'>
									<p className='text-[12px]'>
										{item?.fullfilledPercentage}
										%
									</p>
									{item?.skipMeasurement && (
										<Tooltip title='Skipped by settings'>
											<span className='w-5 h-5 gap-1 items-center'>
												{getNoMachineIcon()}
											</span>
										</Tooltip>
									)}
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${item?.recipeName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(updatedFilters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
									{item?.fullfilledQty == 0 && (
										<Tooltip title='Skipped by operator'>
											<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px]' />
										</Tooltip>
									)}
								</div>
							);
						})}
					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	};

	// produced  summary table data
	const getTableInfoForProducedSummary = () => {
		switch (selectedTab) {
			case 'weight':
				return getWeightProducedBasedTableData();
			case 'cost':
				return getCostProducedBasedTableData();
			case 'percentage':
				return getPercentageBasedProducedBasedTableData();
		}
	};

	const getWeightProducedBasedTableData = () => {
		switch (tableTab) {
			case 'outlets':
				return getProducedOutletsBasedTableData();
			case 'products':
				return getProducedProductsBasedTableData();
			case 'sfg':
				return getProducedSfgBasedTableData();
		}
	};

	const getCostProducedBasedTableData = () => {
		switch (tableTab) {
			case 'outlets':
				return getProducedOutletsCostBasedTableData();
			case 'products':
				return getProducedProductsCostBasedTableData();
			case 'sfg':
				return getProducedSfgCostBasedTableData();
		}
	};

	const getPercentageBasedProducedBasedTableData = () => {
		switch (tableTab) {
			case 'products':
				return getPercentageProducedBasedTableData();
			case 'sfg':
				return getProducedSfgPercentageBasedTableData();
		}
	};

	const generatedData = (data, customRowData = {}) => {
		const { sortColumnName: sortCol, sortType, sortAttribute } = sort
		let convertData = [];
		const podMap = new Map();
		Object.entries(data?.items || {}).forEach(([itemId, item]) => {
			Object.values(item?.pods || {}).forEach((pod) => {
				if (pod?.podId) {
					if (!podMap.has(pod.podId)) {
						podMap.set(pod.podId, {
							podId: pod.podId,
							podName: pod.podName,
							kitchenName: pod.kitchenName,
							scheduledAt: pod.scheduledAt,
							meal: active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.header,
							poIds: pod?.poIds,
							allIngredients: [],
						});
					}
					podMap.get(pod.podId).allIngredients.push({
						ingName: item?.itemName,
						itemClientVariantId: item?.itemClientVariantId,
						...pod,
					});
				}
			});
		});
		convertData = Array.from(podMap.values());

		let result = convertData ?? [];
		const keyName = ['packagedQty', 'dispatchedQty', 'consumedQty', 'leftOverQty'];
		if (sortCol && sortType && customRowData[sortCol]) {
			const renderFn = customRowData[sortCol];
			result.sort((a, b) => {
				const aVal = keyName.includes(sortCol) ? getFirstChildText(renderFn(a)) : getTextFromElement(renderFn(a));
				const bVal = keyName.includes(sortCol) ? getFirstChildText(renderFn(b)) : getTextFromElement(renderFn(b));
				if (sortAttribute === 'number') {
					const numA = extractNumeric(aVal);
					const numB = extractNumeric(bVal);

					if (!isNaN(numA) && !isNaN(numB)) {
						return sortType === 'ascending' ? numA - numB : numB - numA;
					}
				}

				return sortType === 'ascending'
					? String(aVal).localeCompare(String(bVal))
					: String(bVal).localeCompare(String(aVal));
			});
		}

		const searchTerm = search?.searchValue?.toLowerCase()?.trim();
		if (searchTerm) {
			result = result.filter(
				(item) => item?.podName?.toLowerCase()?.trim()?.includes(searchTerm)

			);
		}
		return result;
	};

	const getProducedOutletsBasedTableData = () => {
		const customRowData = {
			outletName: (product) => {
				return <div className='text-[12px] text-blue-500 text-underline cursor-pointer'
					onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: product?.podId, name: product?.podName }))}`)}
				>{product?.podName}</div>;
			},
			orderedQty: (product) => {
				const totalOrderQty = product?.allIngredients?.reduce((acc, item) => acc + (
					item?.ingData?.reduce(
						(subAcc, ingredient) => (subAcc + (ingredient?.orderQty || 0)),
						0
					) || 0
				), 0);
				const unit = product?.allIngredients?.[0]?.unit;
				return (
					<div className='text-[12px]'>
						{totalOrderQty?.toFixed(2)}
						{' '}
						{unit}
					</div>
				);
			},
			actualProducedQty: (product) => {
				const totalFullfilledQty = product?.allIngredients?.reduce((acc, item) => acc + (
					item?.ingData?.reduce(
						(subAcc, ingredient) => (subAcc + (ingredient?.fullfilledQty || 0)),
						0
					) || 0
				), 0);
				const unit = product?.allIngredients?.[0]?.unit;
				return (
					<div className='text-[12px]'>
						{totalFullfilledQty?.toFixed(2)}
						{' '}
						{unit}
					</div>
				);
			},
			productionPerKg: (product) => {
				const totalFullfilledQty = product?.allIngredients?.reduce((acc, item) => acc + (
					item?.ingData?.reduce(
						(subAcc, ingredient) => (subAcc + (ingredient?.fullfilledQty || 0)),
						0
					) || 0
				), 0);
				const totalPackagedCost = product?.allIngredients?.reduce((acc, item) => acc + (item?.packaged * (actualProductPrice?.[item?.ingName] || item?.rate || 0) || 0), 0);
				const productionPerKg = totalPackagedCost / totalFullfilledQty;
				const unit = product?.allIngredients?.[0]?.unit;
				return (
					<div className='text-[12px]'>
						{productionPerKg ? `₹${productionPerKg?.toFixed(2)}` : 0.00}
						{unit ? `/ ${unit}` : null}
					</div>
				);
			},
			productionPercentage: (product) => {
				const totalOrderQty = product?.allIngredients?.reduce((acc, item) => acc + (
					item?.ingData?.reduce(
						(subAcc, ingredient) => (subAcc + (ingredient?.orderQty || 0)),
						0
					) || 0
				), 0);
				const totalFullfilledQty = product?.allIngredients?.reduce((acc, item) => acc + (
					item?.ingData?.reduce(
						(subAcc, ingredient) => (subAcc + (ingredient?.fullfilledQty || 0)),
						0
					) || 0
				), 0);
				const productionPercentage = (totalFullfilledQty / totalOrderQty) * 100;
				return (
					<div className={`text-[12px] ${getTextColor(totalFullfilledQty, totalOrderQty)}`}>
						<Tooltip title={getTooltipTitle(totalFullfilledQty, totalOrderQty, 'produced')}>
							{productionPercentage ? productionPercentage?.toFixed(2) : 0.00}
							%
						</Tooltip>
					</div>
				);
			},
			action: (product) => {
				const convertedData = getConvertedData(data, 'produced', undefined, undefined, actualProductPrice);
				const rowIndex = getRowIndex(data, product?.podId, true, active, parentTab?.id);
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertedData));
				return (
					<SquareArrowOutUpRight
						size={16}
						className='cursor-pointer transition-transform duration-200 hover:scale-125'
						onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=produced&name=${product?.podName}&meal=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&id=${product?.podId}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${rowIndex}`, '_blank')}
					/>
				);
			},
		};
		return {
			data: generatedData(data, customRowData),
			tableRows: customRowData,
			tableColumns: producedSummaryWeightOutletBasedColumns,
		};
	};
	const getProducedOutletsCostBasedTableData = () => {
		const customRowData = {
			outletName: (product) => {
				return <div className='text-[12px] text-blue-500 text-underline cursor-pointer'
					onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: product?.podId, name: product?.podName }))}`)}
				>{product?.podName}</div>;
			},
			orderedCost: (product) => {
				const totalOrderCost = product?.allIngredients?.reduce((acc, item) => acc + (item?.ingData?.reduce((subAcc, ingredient) => subAcc + (ingredient?.orderQty * (actualProductPrice?.[ingredient?.ingName] || ingredient?.rate || 0) || 0), 0) || 0), 0);
				return <div className='text-[12px]'>₹{totalOrderCost?.toFixed(2)}</div>;
			},
			actualProducedCost: (product) => {
				const totalPackagedCost = product?.allIngredients?.reduce((acc, item) => acc + (item?.packaged * (actualProductPrice?.[item?.ingName] || item?.rate || 0) || 0), 0);
				return <div className='text-[12px]'>₹{totalPackagedCost?.toFixed(2)}</div>;
			},
			productionCostPerUnit: (product) => {
				const totalFullfilledQty = product?.allIngredients?.reduce((acc, item) => acc + (
					item?.ingData?.reduce(
						(subAcc, ingredient) => (subAcc + (ingredient?.fullfilledQty || 0)),
						0
					) || 0
				), 0);
				const totalPackagedCost = product?.allIngredients?.reduce((acc, item) => acc + (item?.packaged * (actualProductPrice?.[item?.ingName] || item?.rate || 0) || 0), 0);
				const productionPerKg = totalPackagedCost / totalFullfilledQty;
				const unit = product?.allIngredients?.[0]?.unit;
				return (
					<div className='text-[12px]'>
						{productionPerKg ? `₹${productionPerKg?.toFixed(2)}` : 0.00}
						{unit ? `/ ${unit}` : null}
					</div>
				);
			},
			productionPercentage: (product) => {
				const totalOrderCost = product?.allIngredients?.reduce((acc, item) => acc + (item?.ingData?.reduce((subAcc, ingredient) => subAcc + (ingredient?.orderQty * (actualProductPrice?.[ingredient?.ingName] || ingredient?.rate || 0) || 0), 0) || 0), 0);
				const totalPackagedCost = product?.allIngredients?.reduce((acc, item) => acc + (item?.packaged * (actualProductPrice?.[item?.ingName] || item?.rate || 0) || 0), 0);
				const productionPercentage = (totalPackagedCost / totalOrderCost) * 100;
				return (
					<div className={`text-[12px] ${getTextColor(totalPackagedCost, totalOrderCost)}`}>
						<Tooltip title={getTooltipTitle(totalPackagedCost, totalOrderCost, 'produced')}>
							{productionPercentage ? productionPercentage?.toFixed(2) : 0.00}
							%
						</Tooltip>
					</div>
				);
			},
			action: (product) => {
				const convertedData = getConvertedData(data, 'produced', undefined, undefined, actualProductPrice);
				const rowIndex = getRowIndex(data, product?.podId, true, active, parentTab?.id);
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertedData));
				return (
					<SquareArrowOutUpRight
						size={16}
						className='cursor-pointer transition-transform duration-200 hover:scale-125'
						onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=produced&name=${product?.podName}&meal=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&id=${product?.podId}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${rowIndex}`, '_blank')}
					/>
				);
			},
		};
		return {
			data: generatedData(data, customRowData),
			tableRows: customRowData,
			tableColumns: producedSummaryCostOutletBasedColumns,
		};
	};

	const getProducedProductsBasedTableData = () => {
		const { colMap, columns } = getDynamicColumnMapAndColumns(data);
		const customCol = (column) => {
			if (column?.key === 'ingredientName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			if (column?.key === 'total') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div className='flex flex-col items-center gap-1 justify-center'>
					<p
						className='text-[12px] text-blue-500 text-underline'
						onClick={() => window.open(
							`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: column?.key, name: column?.title }))}`
						)}
					>
						{column?.title}
					</p>
					{getColumnWiseTotal(column?.key, 'producedSummary', 'products', 'weight')}
				</div>
			);
		};
		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const totalFullfilledQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0)), 0);
						return parseFloat(totalFullfilledQty)?.toFixed(2);
					}
				}
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			sortInfo[mapKey] = {
				type: 'number',
				customValue: true,
				value: (product) => {
					const pod = product?.pods?.[mapKey];
					const ingValue = pod?.ingData;
					return ingValue?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0);
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				customRowData[key] = (product) => (
					<div
						className='flex items-start justify-start'
						onClick={() => window.open(
							`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`
						)}
					>
						<p className='text-[12px] text-blue-500 text-underline'>{product?.itemName}</p>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			}
			if (key === 'total') {
				customRowData[key] = (product) => {
					const totalFullfilledQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0)), 0);
					const totalOrderQty = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.orderQty || 0), 0)), 0);
					const unit = Object?.values(product?.pods || {})?.[0]?.ingData?.[0]?.unit;
					return (
						<p className='text-[12px] text-nowrap flex items-center gap-1'>
							{parseFloat(totalFullfilledQty)?.toFixed(2)}
							{' '}
							{unit}
							{' '}
							/
							{' '}
							{parseFloat(totalOrderQty)?.toFixed(2)}
							{' '}
							{unit}
							{totalFullfilledQty === 0 && (
								<Tooltip title='Skipped by operator'>
									<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px] inline-block' />
								</Tooltip>
							)}
						</p>
					);
				};
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				const haveMoreThan2Ing = ingValue?.length > 2;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							return (
								<div className='flex gap-2 text-nowrap items-start justify-start'>
									<div className='flex gap-2 items-center'>
										<span className='text-blue-500 text-[12px]'>
											{Number(item?.fullfilledQty || 0)?.toFixed(2)}
											{' '}
											{item?.unit}
										</span>
										{' '}
										/
										<span className='text-[12px]'>
											{Number(item?.orderQty || 0)?.toFixed(2)}
											{' '}
											{item?.unit}
										</span>
									</div>
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
									{item?.skipMeasurement && (
										<Tooltip title='Skipped by settings'>
											<span className='w-5 h-5 gap-1 items-center'>
												{getNoMachineIcon()}
											</span>
										</Tooltip>
									)}
									{
										item?.fullfilledQty === 0 && (
											<Tooltip title='Skipped by operator'>
												<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px]' />
											</Tooltip>
										)
									}
								</div>
							);
						})}
						{/* {selectedTab === 'weight' && product?.unit !== 'kg' && !haveMoreThan2Ing && (
					<span className='text-[12px]'>{product?.fullfilledWeight} kg / {product?.orderedWeight} kg</span>
				)} */}

					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	};

	const getProducedProductsCostBasedTableData = () => {
		const { colMap, columns } = getDynamicColumnMapAndColumns(data, undefined, 'cost');
		// column custom

		const customCol = (column) => {
			if (column?.key === 'ingredientName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			if (column?.key === 'total') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div
					className='flex flex-col items-center gap-1 justify-center'
					onClick={() => window.open(
						`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: column?.key, name: column?.title }))}`
					)}
				>
					<p className='text-[12px] text-blue-500 text-underline'>
						{column?.title}
					</p>
					{getColumnWiseTotal(column?.key, 'producedSummary', 'products', 'cost')}
				</div>
			);
		};


		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const totalFullfilledCost = Object.values(product?.pods || {})?.reduce((acc, pod) => acc + (pod?.ingData?.reduce((acc, item) => acc + (item?.fullfilledCost || 0), 0)), 0);
						return parseFloat(totalFullfilledCost)?.toFixed(2);
					}
				}
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			sortInfo[mapKey] = {
				type: 'number',
				customValue: true,
				value: (product) => {
					let diffOrderedCostFullfilledCost = 0;
					const pod = product?.pods?.[mapKey];
					const ingValue = pod?.ingData;
					const orderCost = ingValue?.reduce((acc, item) => acc + (Number((item?.orderQty * item?.rate).toFixed(2)) || 0), 0);
					diffOrderedCostFullfilledCost = ingValue?.reduce((acc, item) => acc + (Number(Math.abs((item?.orderQty * item?.rate) - pod?.packagedCost).toFixed(0)) || 0), 0);
					if (orderCost && diffOrderedCostFullfilledCost != 0 && Math.abs((pod?.packagedCost - orderCost) / orderCost) > 0.05) {
						diffOrderedCostFullfilledCost = Number(Math.abs((pod?.packagedCost - orderCost).toFixed(0)));
					} else {
						diffOrderedCostFullfilledCost = 0;
					}
					if (sort?.sortColumnName === 'costPrice') {
						return pod?.packagedCost;
					}
					if (sort?.sortColumnName === 'variance') {
						return diffOrderedCostFullfilledCost;
					}
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				customRowData[key] = (product) => {
					const pod = product?.pods;
					const rate = actualProductPrice?.[product?.itemName] ?? Object?.values(pod)?.[0]?.ingData?.[0]?.rate;
					const unit = Object?.values(pod)?.[0]?.ingData?.[0]?.unit;
					return (
						<div
							className='flex flex-col items-start gap-1 justify-start'
							onClick={() => window.open(
								`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`
							)}
						>
							<div className='flex justify-start'>
								<p className='text-blue-500 text-underline text-[12px]'>{product?.itemName}</p>
								<span className='text-[12px] text-gray-500'>
									(
									{product?.itemClientVariantId}
									)
								</span>
							</div>
							<div className='flex gap-1 justify-start'>
								<span className='text-[12px] font-bold'>Price</span>
								<span className='text-[12px]'>
									₹
									{parseFloat(rate)?.toFixed(2)}
									{' '}
									/
									{unit}
								</span>
							</div>
						</div>
					);
				};
			}
			if (key === 'total') {
				customRowData[key] = (product) => {
					const pod = product?.pods;
					const totalCost = Object?.values(pod)?.reduce((acc, pod) => acc + (pod?.packagedCost || 0), 0);
					return (
						<p className='text-[12px] text-blue-500'>
							₹
							{commaForAmount(totalCost)}
						</p>
					);
				};
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName' || mapKey === 'total') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							const orderCost = Number((item?.orderQty * item?.rate).toFixed(2));
							const diffOrderedCostFullfilledCost = Number(Math.abs((item?.orderQty * item?.rate) - pod?.packagedCost).toFixed(0));
							return (
								<div className='flex gap-2 text-nowrap items-start justify-between'>
									<p className='text-[12px] text-blue-500'>
										₹
										{commaForAmount(pod?.packagedCost)}
									</p>
									<Tooltip title='Indicates (variance) scanned/ordered outside the threshold (±5%)'>
										{orderCost && diffOrderedCostFullfilledCost != 0 && Math.abs((pod?.packagedCost - orderCost) / orderCost) > 0.05 && (
											orderCost > pod?.packagedCost ? (
												<span className='flex items-center gap-1 text-[12px] text-green-500'>
													(
													<ArrowDown size={12} className='text-green-500' />
													{diffOrderedCostFullfilledCost}
													)
												</span>
											) : (
												<span className='flex items-center gap-1 text-[12px] text-red-500'>
													(
													<ArrowUp size={12} className='text-red-500' />
													{diffOrderedCostFullfilledCost}
													)
												</span>
											)
										)}
									</Tooltip>
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: pod?.podId, name: pod?.podName }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
								</div>
							);
						})}
					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	};
	const getPercentageProducedBasedTableData = () => {
		const excludeColumn = ['total'];
		const { colMap, columns } = getDynamicColumnMapAndColumns(data, excludeColumn);

		const customCol = (column) => {
			if (column?.key === 'ingredientName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div
					className='flex flex-col items-center gap-1 justify-center'
					onClick={() => window.open(
						`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: column?.key, name: column?.title }))}`
					)}
				>
					<p className='text-[12px] text-blue-500 text-underline'>{column?.title}</p>
				</div>
			);
		};

		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			}
			for (const [mapKey, columnKey] of colMap || []) {
				if (mapKey === 'ingredientName' || mapKey === 'total') continue;
				sortInfo[mapKey] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const pod = product?.pods?.[mapKey];
						const ingValue = pod?.ingData;
						return pod?.packagedPercent;
					}
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'ingredientName') {
				customRowData[key] = (product) => (
					<div
						className='flex items-center gap-1 justify-start'
						onClick={() => window.open(
							`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPodFilter=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPodFilter=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`
						)}
					>
						<p className='text-[12px] text-blue-500 text-underline'>{product?.itemName}</p>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'ingredientName') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							return (
								<div className='flex items-center gap-1 justify-between'>
									<p className='text-[12px]'>
										{pod?.packagedPercent}
										%
									</p>
									{item?.skipMeasurement && (
										<Tooltip title='Skipped by settings'>
											<span className='w-5 h-5 gap-1 items-center'>
												{getNoMachineIcon()}
											</span>
										</Tooltip>
									)}
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: pod?.podId, name: pod?.podName }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
									{item?.fullfilledQty == 0 && (
										<Tooltip title='Skipped by operator'>
											<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px]' />
										</Tooltip>
									)}
								</div>
							);
						})}
					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};
	};

	const getProducedSfgPercentageBasedTableData = () => {
		const excludeColumn = ['total'];
		const { colMap, columns } = getDynamicColumnMapAndColumns(data, excludeColumn, undefined, 'sfg');

		const customCol = (column) => {
			if (column?.key === 'intermediateName') {
				return <div className='flex items-center gap-1 justify-start'><p className='text-[12px]'>{column?.title}</p></div>;
			}
			return (
				<div
					className='flex flex-col items-center gap-1 justify-center'
					onClick={() => window.open(
						`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: column?.key, name: column?.title }))}`
					)}
				>
					<p className='text-[12px] text-blue-500 text-underline'>{column?.title}</p>
				</div>
			);
		};

		const sortInfo = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'intermediateName') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			}
			for (const [mapKey, columnKey] of colMap || []) {
				if (mapKey === 'intermediateName' || mapKey === 'total') continue;
				sortInfo[mapKey] = {
					type: 'number',
					customValue: true,
					value: (product) => {
						const pod = product?.pods?.[mapKey];
						const ingValue = pod?.ingData;
						return pod?.packagedPercent;
					}
				}
			}
		}
		const customRowData = {};
		for (const [key, columnKey] of colMap || []) {
			if (key === 'intermediateName') {
				customRowData[key] = (product) => (
					<div
						className='flex items-center gap-1 justify-start'
						onClick={() => window.open(
							`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPodFilter=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPodFilter=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: -999 }))}`
						)}
					>
						<p className='text-[12px] text-blue-500 text-underline'>{product?.itemName}</p>
						<span className='text-[12px] text-gray-500'>
							(
							{product?.itemClientVariantId}
							)
						</span>
					</div>
				);
			}
		}
		for (const [mapKey, columnKey] of colMap || []) {
			if (mapKey === 'intermediateName') continue;
			customRowData[mapKey] = (product) => {
				const pod = product?.pods?.[mapKey];
				const ingValue = pod?.ingData;
				if (Array.isArray(ingValue) && ingValue?.length === 0 || !pod) return <p className='text-[12px] flex justify-center'>N/A</p>;
				return (
					<div
						className='flex flex-col gap-2 justify-center '
						style={{
							color: 'black', borderRadius: '10px',
						}}
					>
						{ingValue?.map((item) => {
							return (
								<div className='flex items-center gap-1 justify-between'>
									<p className='text-[12px]'>
										{pod?.packagedPercent}
										%
									</p>
									{item?.skipMeasurement && (
										<Tooltip title='Skipped by settings'>
											<span className='w-5 h-5 gap-1 items-center'>
												{getNoMachineIcon()}
											</span>
										</Tooltip>
									)}
									{item?.fullfilledQty > 0 && (
										<span onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&name=${product?.itemName}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: pod?.podId, name: pod?.podName }))}`)}>
											<Eye className='text-[10px] cursor-pointer transition-transform duration-200 hover:scale-125' />
										</span>
									)}
									{item?.fullfilledQty == 0 && (
										<Tooltip title='Skipped by operator'>
											<img src={assummedIcon} alt='assummedIcon' className='w-[20px] h-[20px]' />
										</Tooltip>
									)}
								</div>
							);
						})}
					</div>
				);
			};
		}
		return {
			data: getFilteredData(Object.values(data?.items || {}), search),
			tableRows: customRowData,
			tableColumns: columns,
			customCol,
			sortInfo,
		};

	}

	// get dispatch summary data
	const getTableInfoForDispatchedSummary = () => {
		switch (selectedTab) {
			case 'weight':
				return getDispatchedWeightBasedTableData();
			case 'cost':
				return getDispatchedCostBasedTableData();
		}
	};

	const getDispatchedWeightBasedTableData = () => {
		const customRowData = {
			outletName: (obj) => {
				return <div className='text-[12px] text-blue-500 text-underline cursor-pointer'
					onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: obj?.podId, name: obj?.podName }))}`)}
				>{obj?.podName}</div>;
			},
			poId: (obj) => {
				let value = null;
				if (Array.isArray(obj?.poIds) && obj?.poIds?.length > 0) {
					value = obj?.poIds;
				} else {
					value = [obj?.poIds];
				}
				return (
					<div className='flex flex-col gap-1'>
						{value.map((item) => (
							<div onClick={() => window.open(`${urls.EXTENSION}${urls.PO_LIST}?poId=${item}&paramNavSelected=${true}`, '_blank')} className='text-[12px] text-blue-500 text-underline cursor-pointer'>{item}</div>
						))}
					</div>
				);
			},
			orderedQty: (obj) => {
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.orderQty, 0);
				return <div className='text-[12px]'>{parseFloat(totalOrderedQty)?.toFixed(2)}</div>;
			},
			packagedQty: (obj) => {
				const totalPackagedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.packaged, 0);
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.orderQty, 0);
				const totalPackagedPercent = totalOrderedQty > 0 ? (totalPackagedQty / totalOrderedQty) * 100 : 0;
				return (
					<div className='text-[12px]'>
						<span>{parseFloat(totalPackagedQty)?.toFixed(2)}</span>
						<span className='text-red-500'>
							(
							{totalPackagedPercent?.toFixed(2)}
							%)
						</span>
					</div>
				);
			},
			dispatchedQty: (obj) => {
				const totalDispatchedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.fullfilledQty, 0);
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.orderQty, 0);
				const totalDispatchedPercent = totalOrderedQty > 0 ? (totalDispatchedQty / totalOrderedQty) * 100 : 0;
				return (
					<div className='text-[12px]'>
						<span>{parseFloat(totalDispatchedQty)?.toFixed(2)}</span>
						<span className='text-red-500'>
							(
							{totalDispatchedPercent?.toFixed(2)}
							%)
						</span>
					</div>
				);
			},
			receivedQty: (obj) => {
				const totalReceivedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.received, 0);
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.orderQty, 0);
				const totalReceivedPercent = totalOrderedQty > 0 ? (totalReceivedQty / totalOrderedQty) * 100 : 0;
				return (
					<div className='text-[12px]'>
						<span>{parseFloat(totalReceivedQty)?.toFixed(2)}</span>
						<span className='text-red-500'>
							(
							{totalReceivedPercent?.toFixed(2)}
							%)
						</span>
					</div>
				);
			},
			transitTime: (obj) => {
				return (
					<div className='text-[12px]'>
						{obj?.transitTime ?? '-'}
					</div>
				);
			},
			vehicleNo: (obj) => {
				const ids = Array.isArray(obj?.poIds) ? obj?.poIds : (obj?.poIds ? [obj?.poIds] : []);
				const items = ids.map((id) => poInfoById?.[id]).filter(Boolean);
				if (!items.length) return <div className='text-[12px]'>-</div>;
				return (
					<div className="flex flex-col gap-1 items-start">
						{items.map((item) => (
							<span key={item?.id} className='text-[12px] text-nowrap'>
								{item?.orderImages?.PRE_DISPATCH?.[0]?.vehicleNumber ?? '-'}
							</span>
						))}
					</div>
				);
			},
			vehicleImg: (obj) => {
				const ids = Array.isArray(obj?.poIds) ? obj?.poIds : (obj?.poIds ? [obj?.poIds] : []);
				const items = ids.map((id) => poInfoById?.[id]).filter(Boolean);
				if (!items.length) return <div className='text-[12px]'>-</div>;
				return (
					<div className="flex flex-col gap-1 items-start w-[9rem]">
						{items.map((item) => (
							<div className='flex gap-2 items-center'>
								{item?.orderImages?.PRE_DISPATCH?.[0]?.imageUrl ? (
									<img onClick={() => handleImagePreview(item?.orderImages?.PRE_DISPATCH?.[0]?.imageUrl)} src={item?.orderImages?.PRE_DISPATCH?.[0]?.imageUrl} alt='vehicleImg' className='w-[50px] h-[50px] rounded-md cursor-pointer' />
								) : null}
								{item?.orderImages?.VERIFICATION_START?.[0]?.imageUrl ? (
									<img onClick={() => handleImagePreview(item?.orderImages?.VERIFICATION_START?.[0]?.imageUrl)} src={item?.orderImages?.VERIFICATION_START?.[0]?.imageUrl} alt='vehicleImg' className='w-[50px] h-[50px] rounded-md cursor-pointer' />
								) : null}
							</div>
						))}
					</div>
				);
			},
			action: (obj) => {
				const convertedData = getConvertedData(data, 'dispatched', undefined, poInfoById, actualProductPrice);
				const rowIndex = getRowIndex(data, obj?.podId, true, active, parentTab?.id);
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertedData));
				return (
					<div className="flex justify-center">
						<SquareArrowOutUpRight
							size={16}
							className='cursor-pointer transition-transform duration-200 hover:scale-125'
							onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=dispatched&name=${obj?.podName}&meal=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&id=${obj?.podId}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${rowIndex}`, '_blank')}
						/>
					</div>
				);
			},
		};
		return {
			data: generatedData(data, customRowData),
			tableRows: customRowData,
			tableColumns: dispatchSummaryWeightOutletBasedColumns,
		};
	};

	const getDispatchedCostBasedTableData = () => {
		const customRowData = {
			outletName: (obj) => {
				return <div className='text-[12px] text-blue-500 text-underline cursor-pointer'
					onClick={() => window.open(`${urls.EXTENSION}${urls?.COOKING_ANALYSIS_LIST}?flag=${true}&mealType=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&subMealValue=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&dateRange=${encodeURIComponent(JSON.stringify(filters?.dateRange))}&fromPod=${encodeURIComponent(JSON.stringify(filters?.fromPodFilter))}&toPod=${filters?.toPodFilter?.id ? encodeURIComponent(JSON.stringify(filters?.toPodFilter)) : encodeURIComponent(JSON.stringify({ id: obj?.podId, name: obj?.podName }))}`)}
				>{obj?.podName}</div>;
			},
			poId: (obj) => {
				return <div className='text-[12px]'>{obj?.poId}</div>;
			},
			orderedAmount: (obj) => {
				const totalOrderedAmount = obj?.allIngredients?.reduce((acc, item) => acc + ((item?.orderQty * (actualProductPrice?.[item?.ingName] || item?.rate)) ?? item?.orderCost), 0);
				return (
					<div className='text-[12px]'>
						₹
						{totalOrderedAmount?.toFixed(2)}
					</div>
				);
			},
			packagedAmount: (obj) => {
				const totalPackagedAmount = obj?.allIngredients?.reduce((acc, item) => acc + ((item?.packaged * (actualProductPrice?.[item?.ingName] || item?.rate)) ?? item?.packagedCost), 0);
				return (
					<div className='text-[12px]'>
						₹
						{totalPackagedAmount?.toFixed(2)}
					</div>
				);
			},
			dispatchedAmount: (obj) => {
				const totalDispatchedAmount = obj?.allIngredients?.reduce((acc, item) => acc + ((item?.fullfilledQty * (actualProductPrice?.[item?.ingName] || item?.rate)) ?? item?.fullfilledCost), 0);
				return (
					<div className='text-[12px]'>
						₹
						{totalDispatchedAmount?.toFixed(2)}
					</div>
				);
			},
			receivedAmount: (obj) => {
				const totalReceivedAmount = obj?.allIngredients?.reduce((acc, item) => acc + ((item?.received * (actualProductPrice?.[item?.ingName] || item?.rate)) ?? item?.receivedCost), 0);
				return (
					<div className='text-[12px]'>
						₹
						{totalReceivedAmount?.toFixed(2)}
					</div>
				);
			},
			action: (obj) => {
				const convertedData = getConvertedData(data, 'dispatched', undefined, undefined, actualProductPrice);
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertedData));
				return (
					<SquareArrowOutUpRight
						size={16}
						className='cursor-pointer transition-transform duration-200 hover:scale-125'
						onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=dispatched&name=${obj?.podName}&meal=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&id=${obj?.podId}&filters=${encodeURIComponent(JSON.stringify(filters))}`, '_blank')}
					/>
				);
			},
		};
		return {
			data: generatedData(data, customRowData),
			tableRows: customRowData,
			tableColumns: dispatchSummaryCostOutletBasedColumns,
		};
	};

	// get restaurant indent data
	const getTableInfoForRestaurantIndent = () => {
		switch (selectedTab) {
			case 'weight':
				return getSectionWeightBased();
			case 'cost':
				return getSectionCostBased();
		}
	};

	const getSectionWeightBased = () => {
		const customRowData = {
			sectionName: (obj) => {
				return <div className='text-[12px]'>{obj?.podName}</div>;
			},
			orderedQty: (obj) => {
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + item?.orderQty, 0);
				return (
					<div className='text-[12px]'>
						{totalOrderedQty?.toFixed(2)}
						{' '}
						{obj?.unit}
					</div>
				);
			},
			dispatchedQty: (obj) => {
				const totalDispatchedQty = obj?.allIngredients?.reduce((acc, item) => acc + (item?.fullfilledQty || 0), 0);
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + (item?.orderQty || 0), 0);
				const totalPercent = totalOrderedQty > 0 ? (totalDispatchedQty / totalOrderedQty) * 100 : 0;
				return (
					<div className='text-[12px]'>
						<span>
							{totalDispatchedQty?.toFixed(2)}
							{' '}
							{obj?.unit}
						</span>
						<span className='text-red-500'>
							(
							{totalPercent?.toFixed(2)}
							%)
						</span>
					</div>
				);
			},
			leftOverQty: (obj) => {
				const totalLeftOverQty = obj?.allIngredients?.reduce((acc, item) => acc + (item?.leftOverQty || 0), 0);
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + (item?.orderQty || 0), 0);
				const totalPercent = totalOrderedQty > 0 ? (totalLeftOverQty / totalOrderedQty) * 100 : 0;
				return (
					<div className='text-[12px]'>
						<span>
							{totalLeftOverQty?.toFixed(2)}
							{' '}
							{obj?.unit}
						</span>
						<span className='text-red-500'>
							(
							{totalPercent?.toFixed(2)}
							%)
						</span>
					</div>
				);
			},
			consumedQty: (obj) => {
				const totalConsumedQty = obj?.allIngredients?.reduce((acc, item) => acc + (item?.consumptionQty || 0), 0);
				const totalOrderedQty = obj?.allIngredients?.reduce((acc, item) => acc + (item?.orderQty || 0), 0);
				const totalPercent = totalOrderedQty > 0 ? (totalConsumedQty / totalOrderedQty) * 100 : 0;
				if (totalConsumedQty > 0) {
					return (
						<div className='text-[12px]'>
							<span>
								{totalConsumedQty?.toFixed(2)}
								{' '}
								{obj?.unit}
							</span>
							<span className='text-red-500'>
								(
								{totalPercent?.toFixed(2)}
								%)
							</span>
						</div>
					);
				}
				return <div className='text-[12px]'>0.00</div>
			},
			action: (obj) => {
				const convertedData = getConvertedData(data, 'restaurantIndent');
				const rowIndex = getRowIndex(data, obj?.podId, true, active, parentTab?.id);
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertedData));
				return (
					<SquareArrowOutUpRight
						size={16}
						className='cursor-pointer transition-transform duration-200 hover:scale-125'
						onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=restaurantIndent&name=${obj?.podName}&meal=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&id=${obj?.podId}&filters=${encodeURIComponent(JSON.stringify(filters))}&rowIndex=${rowIndex}`, '_blank')}
					/>
				);
			},
		};
		return {
			data: generatedData(data, customRowData),
			tableRows: customRowData,
			tableColumns: restaurantIndentWeightSectionBasedColumns,
		};
	};

	const getSectionCostBased = () => {
		const customRowData = {
			sectionName: (obj) => {
				return <div className='text-[12px]'>{obj?.podName}</div>;
			},
			orderedAmount: (obj) => {
				const totalOrderedAmount = obj?.allIngredients?.reduce((acc, item) => acc + (item?.orderCost ?? item?.orderQty * item?.rate), 0);
				return (
					<div className='text-[12px]'>
						₹
						{totalOrderedAmount?.toFixed(2)}
					</div>
				);
			},
			dispatchedAmount: (obj) => {
				const totalDispatchedAmount = obj?.allIngredients?.reduce((acc, item) => acc + (item?.fullfilledCost ?? item?.fullfilledQty * item?.rate), 0);
				return (
					<div className='text-[12px]'>
						₹
						{totalDispatchedAmount?.toFixed(2)}
					</div>
				);
			},
			leftOverAmount: (obj) => {
				const totalLeftOverAmount = obj?.allIngredients?.reduce((acc, item) => acc + (item?.leftOverCost ?? item?.leftOverQty * item?.rate), 0);
				return (
					<div className='text-[12px]'>
						₹
						{totalLeftOverAmount?.toFixed(2)}
					</div>
				);
			},
			consumedAmount: (obj) => {
				const totalConsumedAmount = obj?.allIngredients?.reduce((acc, item) => acc + (item?.consumptionCost ?? item?.consumptionQty * item?.rate), 0);
				if (totalConsumedAmount > 0) {
					return (
						<div className='text-[12px]'>
							₹
							{totalConsumedAmount?.toFixed(2)}
						</div>
					);
				}
				return <div className='text-[12px]'>₹0.00</div>
			},
			action: (obj) => {
				const convertedData = getConvertedData(data, 'restaurantIndent');
				sessionStorage.setItem('aiFmsProductList', JSON.stringify(convertedData));
				return (
					<SquareArrowOutUpRight
						size={16}
						className='cursor-pointer transition-transform duration-200 hover:scale-125'
						onClick={() => window.open(`${urls.EXTENSION}${urls.AI_FMS_DETAIL_PAGE}?mode=restaurantIndent&name=${obj?.podName}&meal=${active?.shiftName?.toLowerCase() === 'day' ? 'allDay' : active?.shiftName?.toLowerCase()}&id=${obj?.podId}&filters=${encodeURIComponent(JSON.stringify(filters))}`, '_blank')}
					/>
				);
			},
		};
		return {
			data: generatedData(data, customRowData),
			tableRows: customRowData,
			tableColumns: restaurantIndentCostSectionBasedColumns,
		};
	};
	// get wastage summary
	const getTableInfoForWastageSummary = () => {
		switch (selectedTab) {
			case 'weight':
				return getWastageSummaryWeight();
			case 'cost':
				return getWastageSummaryCost();
		}
	};

	const getWastageSummaryWeight = () => {
		switch (tableTab) {
			case 'processWise':
				return getWastageSummaryProcessWiseWeight();
			case 'reasonWise':
				return getWastageSummaryReasonWiseWeight();
			case 'finishGoodsWise':
				return getWastageSummaryFinishGoodsWiseWeight();
		}
	}

	const getWastageSummaryCost = () => {
		switch (tableTab) {
			case 'processWise':
				return getWastageSummaryProcessWiseCost();
			case 'reasonWise':
				return getWastageSummaryReasonWiseCost();
			case 'finishGoodsWise':
				return getWastageSummaryFinishGoodsWiseCost();
		}
	};
	// wastage weight wise functions
	const getWastageSummaryProcessWiseWeight = () => {
		const { tableColumns } = getDynamicWastageColumns(data, 'processWiseWatage');
		const customCol = (obj) => {
			return <div className='text-[12px]'>{obj?.title}</div>;
		}
		const customRowData = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				customRowData[key] = (obj) => {
					return <div className='text-[12px]'>{obj?.itemName}</div>;
				}
				continue;
			}
			if (key === 'total') {
				customRowData[key] = (obj) => {
					const total = obj?.processWiseWatage?.reduce((acc, item) => acc + item?.qty, 0);
					const unit = getHigherUnitName(obj?.unit);
					return <div className='text-[12px]'>{total ? `${total.toFixed(2)} ${unit}` : '-'} </div>;
				}
				continue;
			}
			customRowData[key] = (obj) => {
				const wastageQty = obj?.processWiseWatage?.find((item) => item?.name === key)?.qty;
				const unit = getHigherUnitName(obj?.unit);
				return <div className='text-[12px]'>{wastageQty ? `${wastageQty.toFixed(2)} ${unit}` : '-'} </div>;
			}
		}

		const sortInfo = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const total = obj?.processWiseWatage?.reduce((acc, item) => acc + item?.qty, 0);
						return parseFloat(total?.toFixed(2));
					}
				}
			} else {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const qty = obj?.processWiseWatage?.find((item) => item?.name === key)?.qty;
						return parseFloat(qty?.toFixed(2))
					}
				}
			}
		}
		return {
			data: getfilteredWastageData(data, search),
			tableRows: customRowData,
			tableColumns,
			customCol,
			sortInfo,
		};
	}
	const getWastageSummaryReasonWiseWeight = () => {
		const { tableColumns } = getDynamicWastageColumns(data, 'reasonWiseWatage');
		const customCol = (obj) => {
			return <div className='text-[12px]'>{obj?.title}</div>;
		}
		const customRowData = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				customRowData[key] = (obj) => {
					return <div className='text-[12px]'>{obj?.itemName}</div>;
				}
				continue;
			}
			if (key === 'total') {
				customRowData[key] = (obj) => {
					const total = obj?.reasonWiseWatage?.reduce((acc, item) => acc + item?.qty, 0);
					const unit = getHigherUnitName(obj?.unit);
					return <div className='text-[12px]'>{total ? `${total.toFixed(2)} ${unit}` : '-'} </div>;
				}
				continue;
			}
			customRowData[key] = (obj) => {
				const wastageQty = obj?.reasonWiseWatage?.find((item) => item?.name === key)?.qty;
				const unit = getHigherUnitName(obj?.unit);
				return <div className='text-[12px]'>{wastageQty ? `${wastageQty.toFixed(2)} ${unit}` : '-'} </div>;
			}
		}

		const sortInfo = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const total = obj?.reasonWiseWatage?.reduce((acc, item) => acc + item?.qty, 0);
						return parseFloat(total?.toFixed(2));
					}
				}
			} else {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const qty = obj?.reasonWiseWatage?.find((item) => item?.name === key)?.qty;
						return parseFloat(qty?.toFixed(2))
					}
				}
			}
		}
		return {
			data: getfilteredWastageData(data, search),
			tableRows: customRowData,
			tableColumns,
			customCol,
			sortInfo,
		};
	}
	const getWastageSummaryFinishGoodsWiseWeight = () => { }

	// wastage cost wise functions
	const getWastageSummaryProcessWiseCost = () => {
		const { tableColumns } = getDynamicWastageColumns(data, 'processWiseWatage');
		const customCol = (obj) => {
			return <div className='text-[12px]'>{obj?.title}</div>;
		}
		const customRowData = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				customRowData[key] = (obj) => {
					return <div className='text-[12px]'>{obj?.itemName}</div>;
				}
				continue;
			}
			if (key === 'total') {
				customRowData[key] = (obj) => {
					const total = obj?.processWiseWatage?.reduce((acc, item) => acc + item?.cost, 0);
					return <div className='text-[12px]'>{total ? `₹${total.toFixed(2)}` : '-'} </div>;
				}
				continue;
			}
			customRowData[key] = (obj) => {
				const wastageQty = obj?.processWiseWatage?.find((item) => item?.name === key)?.cost;
				return <div className='text-[12px]'>{wastageQty ? `₹${wastageQty.toFixed(2)}` : '-'} </div>;
			}
		}

		const sortInfo = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const total = obj?.processWiseWatage?.reduce((acc, item) => acc + item?.cost, 0);
						return parseFloat(total?.toFixed(2));
					}
				}
			} else {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const qty = obj?.processWiseWatage?.find((item) => item?.name === key)?.cost;
						return parseFloat(qty?.toFixed(2))
					}
				}
			}
		}

		return {
			data: getfilteredWastageData(data, search),
			tableRows: customRowData,
			tableColumns,
			customCol,
			sortInfo,
		};
	}
	const getWastageSummaryReasonWiseCost = () => {
		const { tableColumns } = getDynamicWastageColumns(data, 'reasonWiseWatage');
		const customCol = (obj) => {
			return <div className='text-[12px]'>{obj?.title}</div>;
		}
		const customRowData = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				customRowData[key] = (obj) => {
					return <div className='text-[12px]'>{obj?.itemName}</div>;
				}
				continue;
			}
			if (key === 'total') {
				customRowData[key] = (obj) => {
					const total = obj?.reasonWiseWatage?.reduce((acc, item) => acc + item?.cost, 0);
					return <div className='text-[12px]'>{total ? `₹${total.toFixed(2)}` : '-'} </div>;
				}
				continue;
			}
			customRowData[key] = (obj) => {
				const wastageQty = obj?.reasonWiseWatage?.find((item) => item?.name === key)?.cost;
				return <div className='text-[12px]'>{wastageQty ? `₹${wastageQty.toFixed(2)}` : '-'} </div>;
			}
		}
		const sortInfo = {};
		for (const { key, title } of tableColumns) {
			if (key === 'itemList') {
				sortInfo[key] = {
					type: 'string',
					customValue: true,
					value: (obj) => {
						return obj?.itemName ?? '';
					},
				}
			} else if (key === 'total') {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const total = obj?.reasonWiseWatage?.reduce((acc, item) => acc + item?.cost, 0);
						return parseFloat(total?.toFixed(2));
					}
				}
			} else {
				sortInfo[key] = {
					type: 'number',
					customValue: true,
					value: (obj) => {
						const qty = obj?.reasonWiseWatage?.find((item) => item?.name === key)?.cost;
						return parseFloat(qty?.toFixed(2))
					}
				}
			}
		}

		return {
			data: getfilteredWastageData(data, search),
			tableRows: customRowData,
			tableColumns,
			customCol,
			sortInfo,
		};
	}
	const getWastageSummaryFinishGoodsWiseCost = () => { }

	const getTableDataBySubTabs = () => {
		switch (subParentTab?.id) {
			case 1:
				return getTableInfoForIssuedSummary();
			case 2:
				return getTableInfoForRestaurantIndent();
		}
	};

	const getTableInfo = useCallback(() => {
		switch (parentTab?.id) {
			case 1:
				return getTableDataBySubTabs();
			case 2:
				return getTableInfoForProducedSummary();
			case 3:
				return getTableInfoForDispatchedSummary();
			case 4:
				return getTableInfoForWastageSummary();
		}
	}, [parentTab, selectedTab, tableTab, data, search, sort, poInfoById, actualProductPrice, subParentTab]);

	return { getTableInfo };
};